import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { SplitButton } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { setContentManagerData, setSelectedContentItem, setUploadContentManagerFiles } from "../../../../actions/contentManager";
import { FolderNameDialog } from "../../../../components/ContentManagementSystem/ContentManager/Dialog";
import { addFolder, addYDoc, doxSvgIcon, isValidSelectedFolder, searchTreeItem } from "../../../../components/ContentManagementSystem/helper";
import { button, icon, label, notifyIcon, quote, route, tooltip } from "../../../../config";
import { getNotification } from "../../../../utils/common";
import { getContentAttachmentsCount } from "../../../services/contentManger.services";

/**
 * MultiUploadDropdown component to handle multiple file management actions
 * such as uploading files, adding folders, tasks, and reminders.
 *
 * @returns {JSX.Element} The rendered component.
 * @author Muskan Thakur
 */
const MultiUploadDropdown = ({ handleAddTask, handleAddReminder, popupSettings, projectId, projectName }) => {
  const { selectedContentManagerItem, contentManagerData } = useSelector((state) => state.contentManager);
  const dispatch = useDispatch();
  const isQuickAccessSelected = selectedContentManagerItem?.id === label.QUICK_ACCESS_ROOT;
  const invalidIdsforAddingAttachments = ["recentRoot", "sharedWithMeRoot", "trashRoot", "taskAssetsRoot"];
  const history = useHistory();
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [showDoxDialog, setShowDoxDialog] = useState(false);
  const [defaultName, setDefaultName] = useState("");

  /**
   * Creates the click handler function for each dropdown item.
   */
  const handleClick = (handler) => (e) => {
    e.preventDefault();
    if (handler) {
      handler();
    }
  };

  /** Handles the logic to add a folder, either inside a selected folder or at the root level */
  const handleAddFolder = async () => {
    const isInFilesAndFolder =
      selectedContentManagerItem?.id === `${label.PROJECT_ROOT}_${projectId}` ||
      selectedContentManagerItem?.id === label.QUICK_ACCESS_ROOT ||
      selectedContentManagerItem?.path?.includes(`/${label.FILES_AND_FOLDER_DOCUMENTS}/`) ||
      selectedContentManagerItem?.path?.endsWith(`/${label.FILES_AND_FOLDER_DOCUMENTS}`) ||
      selectedContentManagerItem?.path?.includes(`/${label.QUICK_ACCESS}/`) ||
      selectedContentManagerItem?.path?.endsWith(`/${label.QUICK_ACCESS}`);

    if (!selectedContentManagerItem?.id || !isInFilesAndFolder) {
      return getNotification(quote.SELECT_FOLDER, notifyIcon.WARNING_ICON);
    }
    const folderCounter = await getContentAttachmentsCount({ projectId: projectId });
    const defaultFolderName = folderCounter ? `${label.NEW_FOLDER} (${folderCounter})` : label.NEW_FOLDER;
    setDefaultName(defaultFolderName);
    setShowFolderDialog(true);
  };

  /** Closes the folder dialog */
  const handleDialogClose = () => {
    setShowFolderDialog(false);
    setShowDoxDialog(false);
  };

  /** Handles folder creation when dialog is confirmed */
  const handleFolderDialogClick = async (event) => {
    if (event.type === "create") {
      const folderCounter = await getContentAttachmentsCount({ projectId: projectId });
      let newData;

      if (selectedContentManagerItem) {
        newData = await addFolder(contentManagerData, selectedContentManagerItem, folderCounter, projectId, event.value);
        const newTreeItem = searchTreeItem(newData, selectedContentManagerItem);
        dispatch(setSelectedContentItem(newTreeItem));
      } else {
        newData = await addFolder(contentManagerData, null, folderCounter, projectId, event.value);
      }

      dispatch(setContentManagerData(newData));
      setShowFolderDialog(false);
    }
  };

  /**
   * Handles the upload action, dispatching to trigger the file upload modal.
   */
  const handleUpload = () => {
    const isInFilesAndFolder =
      selectedContentManagerItem?.id === `${label.PROJECT_ROOT}_${projectId}` ||
      selectedContentManagerItem?.id === label.QUICK_ACCESS_ROOT ||
      selectedContentManagerItem?.path?.includes(`/${label.FILES_AND_FOLDER_DOCUMENTS}/`) ||
      selectedContentManagerItem?.path?.endsWith(`/${label.FILES_AND_FOLDER_DOCUMENTS}`) ||
      selectedContentManagerItem?.path?.includes(`/${label.QUICK_ACCESS}/`) ||
      selectedContentManagerItem?.path?.endsWith(`/${label.QUICK_ACCESS}`);

    if (!selectedContentManagerItem?.id || !isInFilesAndFolder) {
      return getNotification(quote.SELECT_FOLDER, notifyIcon.WARNING_ICON);
    } else dispatch(setUploadContentManagerFiles(true));
  };

  /**
   * Handle YDoc
   */
  const handleDox = async () => {
    const isInFilesAndFolder =
      selectedContentManagerItem?.id === `${label.PROJECT_ROOT}_${projectId}` ||
      selectedContentManagerItem?.id === label.QUICK_ACCESS_ROOT ||
      selectedContentManagerItem?.path?.includes(`/${label.FILES_AND_FOLDER_DOCUMENTS}/`) ||
      selectedContentManagerItem?.path?.endsWith(`/${label.FILES_AND_FOLDER_DOCUMENTS}`) ||
      selectedContentManagerItem?.path?.includes(`/${label.QUICK_ACCESS}/`) ||
      selectedContentManagerItem?.path?.endsWith(`/${label.QUICK_ACCESS}`);

    if (!selectedContentManagerItem?.id || !isInFilesAndFolder) {
      return getNotification(quote.SELECT_FOLDER, notifyIcon.WARNING_ICON);
    }
    setDefaultName(label.UNTITLED);
    setShowDoxDialog(true);
  };

  /** Handles dox creation when dialog is confirmed */
  const handleDoxDialogClick = async (event) => {
    if (event.type === "create") {
      const newData = await addYDoc(contentManagerData, selectedContentManagerItem || null, null, projectId, event.value);
      dispatch(setContentManagerData(newData.updatedData));
      if (selectedContentManagerItem) {
        const newTreeItem = searchTreeItem(newData.updatedData, selectedContentManagerItem);
        dispatch(setSelectedContentItem(newTreeItem));
      }
      if (newData.newYDocId) {
        history.push(`${route.PRIVATE_ROUTE.Y_DOC.PATH}/${newData.newYDocId}`);
      }
      setShowDoxDialog(false);
    }
  };

  const items = useMemo(() => {
    const allItems = [
      {
        text: button.ADD_FOLDER,
        icon: icon.FOLDER,
        onClick: handleClick(handleAddFolder),
      },
      {
        text: label.ADD_TASK,
        icon: icon.ADD_TASK,
        onClick: handleClick(handleAddTask),
      },
      {
        text: label.ADD_REM,
        icon: icon.WORKFLOW_9,
        onClick: handleClick(handleAddReminder),
      },
    ];
    return isQuickAccessSelected ? allItems.filter((item) => item.text !== button.ADD_FOLDER) : allItems;
  }, [handleAddTask, handleAddReminder, handleUpload, handleAddFolder, isQuickAccessSelected]);

  return (
    <>
      <SplitButton
        className='btn-width btn btn-primary mr-4'
        popupSettings={popupSettings}
        text={
          isQuickAccessSelected ? (
            <span id='header-multi-add-button-cms' title={label.UPLOAD_FILE}>
              {icon.FILE_UPLOAD}{" "}
              {label.UPLOAD_FILE}
            </span>
          ) : (
            <span id='header-multi-add-button-cms' title={label.ADD_DOX}>
              <span className='dox-icon'>
                <SvgIcon icon={doxSvgIcon} />
              </span>{" "}
              {label.ADD_DOX}
            </span>
          )
        }
        items={items.map((item) => ({
          ...item,
          render: () => (
            <div className='d-flex align-items-center w-100 px-3' onClick={item.onClick}>
              {item.icon}
              <span className='ml-2'>{item.text}</span>
            </div>
          ),
        }))}
        onButtonClick={isQuickAccessSelected ? handleUpload : handleDox}
      />
      {showFolderDialog && <FolderNameDialog defaultName={defaultName} onDialogClose={handleDialogClose} onDialogClick={handleFolderDialogClick} />}
      {showDoxDialog && (
        <FolderNameDialog
          isDox={true}
          title={tooltip.ADD_NEW_DOCUMENT}
          defaultName={defaultName}
          onDialogClose={handleDialogClose}
          onDialogClick={handleDoxDialogClick}
        />
      )}
    </>
  );
};

export default MultiUploadDropdown;
